import SeekerStore from "./seekerStore";
import { action, observable, IObservableArray } from "mobx";
import { ScreenMode, View, BusyEvent } from "../models/enums";
import { FAQ } from "../models/help";
import { hashHistory as history } from "react-router";
import FeaturedContent from "../models/featuredContent";
import { Auth } from "../utils/auth";
import Fetcher from "../utils/fetcher";
import { copyArray } from "../utils/data";

interface IViewPath {
  vw: View;
  path: string;
}

// Set up mapping between top-level views and react-router paths (Should this reside elsewhere?)
const viewPaths: IViewPath[] = [
  { vw: View.Opportunities, path: "/home" },
  { vw: View.Profile, path: "/profile" }];

export default class ViewState {
  private _store: SeekerStore;
  @observable acceptTerms: number;
  @observable registerStep: number;
  @observable showDrawer: boolean;
  @observable showLogin: boolean;
  @observable showRegister: boolean;
  @observable showPasswordReset: boolean;
  @observable showPasswordChange: boolean;
  @observable showOpportunity: boolean;
  @observable showLanguages: boolean;
  @observable showAddExperienceVw: boolean;
  @observable screenMode: ScreenMode;
  @observable currentView: View;
  @observable featuredContent: Array<FeaturedContent>;
  @observable showExternalLink: boolean;
  @observable externalLinkIgnore: boolean;
  @observable externalLinkUrl?: string;
  @observable faqs: Array<FAQ>;
  @observable showSnackbar: boolean;
  @observable snackbarMessage: string;
  @observable showSnackBarAction: boolean;
  @observable snackBarActionText: string;
  @observable snackBarActionLink: string;
  @observable busyFlag: number;
  @observable showConfirmationDlg: boolean;
  @observable showAddGraduationDlg: boolean;
  @observable scrollToSearchBar: boolean;
  @observable scrollToRecommendedOps: boolean;

  constructor(store: SeekerStore) {
    this._store = store;
    this.showDrawer = false;
    this.showLogin = false;
    this.showRegister = false;
    this.showPasswordReset = false;
    this.showPasswordChange = false;
    this.showOpportunity = false;
    this.showLanguages = false;
    this.showAddExperienceVw = false;
    this.acceptTerms = 0;
    this.registerStep = 1;
    this.currentView = View.Undefined;
    this.showExternalLink = false;
    this.externalLinkUrl = null;
    window.addEventListener("resize", this.updateScreenMode);
    this.updateScreenMode();
    this.setView(View.Opportunities);
    this.featuredContent = new Array<FeaturedContent>();
    this.initFeaturedContent();
    (window as any).seekerViewState = this;
    this.externalLinkIgnore = Boolean(localStorage.getItem(Auth.linkKey));
    this.faqs = new Array<FAQ>();
    this._store.languageCallbacks.push(this.clearFAQs);
    this.busyFlag = 0;
    this.showSnackbar = false;
    this.snackbarMessage = null;
    this.showSnackBarAction = false;
    this.snackBarActionLink = null;
    this.snackBarActionText = null;
    this.showConfirmationDlg = false;
    this.showAddGraduationDlg = false;
    this.scrollToSearchBar = false;
    this.scrollToRecommendedOps = false;
  }
  // External link support
  showLink(url: string) {
    this.setShowExternalLink(true, url);
  }
  convertStaticLinks = (text: string): string => {
    if (!text) { return null; }
    let s: string = text.replace(/href=['"](.*?)['"]/g, function (m, $1) {
      return `href="#" onClick="{window.seekerViewState.showLink('${$1}');return false;}"`;
    });
    return s;
  }
  clearData = (): void => {
    this._store.educationStore.clear();
    this._store.legal.clear();
    this._store.opportunityStore.clear();
    this._store.profileStore.clear();   
    this.initFeaturedContent();
  }
  initPath = (): void => {
    let route: string = "/";
    if (Auth.isAuthenticated) {
      route = Auth.lastRoute;
      if (this._store.profileStore && !this._store.profileStore.profile) {
        this._store.profileStore.loadProfile();
      }
      if (!Auth.reAuthenticated || !route) {
        if (Auth.loginCt <= 1) {
          route = this._store.localization.fields.routes.authFirstTime;
        } else {
          route = this._store.localization.fields.routes.auth;
        }
      }
    } else {
      route = this._store.localization.fields.routes.anonymous;
    }
    const test: string = `#${route}`;
    if (window.location.hash !== test) {
      history.push(route);
    }
  }
  @action setShowExternalLink(show: boolean, url?: string): void {
    if (show && url) {
      if (this.externalLinkIgnore) {
        window.open(url, "_blank");
      } else {
        this.showExternalLink = show;
        this.externalLinkUrl = url;
      }
    } else {
      this.showExternalLink = false;
      this.externalLinkUrl = null;
    }
  }
  @action setIgnoreLinkPrompt = (ignore: boolean): void => {
    this.externalLinkIgnore = ignore;
    localStorage.setItem(Auth.linkKey, ignore.toString());
  }
  // Busy State
  @action setBusyBit = (evt: BusyEvent): void => {
    this.busyFlag |= evt;
  }
  @action clearBusyBit = (evt: BusyEvent): void => {
    this.busyFlag &= ~evt;
  }
  isBusy = (evt: BusyEvent): boolean => {
    return (this.busyFlag & evt) > 0;
  }
  @action updateScreenMode = (): void => {
    const width: number = window.innerWidth;
    if (width <= 767) {
      this.screenMode = ScreenMode.Mobile;
      return;
    } else if (width <= 991) {
      this.screenMode = ScreenMode.Tablet;
      return;
    } else {
      this.screenMode = ScreenMode.Desktop;
      return;
    }
    
  }
  initFeaturedContent = (): void => {
    let url: string = "api/featuredcontent";
    if (Auth.isAuthenticated) {
      url += "/authorized";
    }
    Fetcher.get(url, action((features: Array<FeaturedContent>): void => {
      copyArray(features, this.featuredContent, (fc: FeaturedContent) => { return new FeaturedContent(fc); });
    }));
  }

  @action setScrollToSearchBar = (scroll: boolean): void => {
    this.scrollToSearchBar = scroll;
  }

  @action setScrollToRecommendedOps = (scroll: boolean): void => {
    this.scrollToRecommendedOps = scroll;
  }

  @action setShowSnackbar = (show: boolean, msg?: string, showAction?: boolean, actionText?: string, actionLink?: string): void => {
    this.showSnackbar = show;
    this.snackbarMessage = msg;
    this.showSnackBarAction = showAction;
    this.snackBarActionText = actionText;
    this.snackBarActionLink = actionLink;
  }

  @action closeSnackbar = (): void => {
    this.setShowSnackbar(false);
    this.showSnackBarAction = false;
  }

  @action setShowGraduationDlg = (show: boolean): void => {
    this.showAddGraduationDlg = show;
  }

  closeAddGraduationDlg = (): void => {
    this.setShowGraduationDlg(false);
  }
  /* Drawer functions */
  @action toggleDrawer = (): void => {
    this.showDrawer = !this.showDrawer;
  }

  @action closeDrawer = (): void => {
    this.showDrawer = false;
  }

  /* Login functions */
  @action setShowLogin = (show: boolean): void => {
    this.showLogin = show;
    this.showDrawer = false;
  }

  closeLogin = (): void => {
    this.setShowLogin(false);
  }

  @action setShowConfirmationDlg = (show: boolean): void => {
    this.showConfirmationDlg = show;
  }

  closeConfirmationDlg = (): void => {
    this.setShowConfirmationDlg(false);
  }

  /* Register functions */
  @action setShowRegister = (show: boolean): void => {
    this.showRegister = show;
    this.showDrawer = false;
    this.showLogin = false;
  }

  closeRegistration = (): void => {
    this.setShowRegister(false);
  }

  @action setShowOpportunity = (show: boolean): void => {
    this.showOpportunity = show;
  }

  closeOpportunity = (): void => {
    this.setShowOpportunity(false);
  }

  @action setShowLanguages = (show: boolean): void => {
    this.showLanguages = show;
  }

  closeLanguages = (): void => {
    this.setShowLanguages(false);
  }
  /* Password Reset functions */
  @action setShowPasswordReset = (show: boolean): void => {
    this.showPasswordReset = show;
  }
  closePasswordReset = (): void => {
    this.setShowPasswordReset(false);
  }
  @action setShowPasswordChange = (show: boolean): void => {
    this.showPasswordChange = show;
  }
  /* Registration page function */
  @action setRegisterStep = (step: number): void => {
    this.registerStep = step;
  }

  /* Current view management */
  @action setView = (vw: View): void => {
    this.currentView = vw;
    const currPath: string = window.location.hash;
    let newPath: string = "";
    viewPaths.map((p: IViewPath): void => {
      if (p.vw === vw) {
        newPath = p.path;
      }
    });
    if (currPath !== newPath) {
      history.push(newPath);
    }
  }
  trackView = (pg?: string): void => {
    pg = pg || window.location.hash.replace("#", "");
    sessionStorage.setItem(Auth.viewKey, pg);
  }
  returnToLastView = (): void => {
    const pg: string = sessionStorage.getItem(Auth.viewKey);
    if (pg && Auth.isAuthenticated) {
      history.push(pg);
    }
  }
  requireAuthentication = (track: boolean = true): boolean => {
    if (track) {
      this.trackView();
    }
    if (!Auth.isAuthenticated) {
      const pg: string = window.location.hash.replace("#", "");
      history.push(this._store.localization.fields.routes.anonymous);
      this.trackView(pg);
      this.setShowLogin(true);
      return false;
    }
    return true;
  }
  @action clearFAQs = (): void => {
    this.faqs = new Array<FAQ>();
  }
  loadFAQs(): void {
    if (!this.faqs || this.faqs.length === 0) {
      Fetcher.get("api/faqs?app=seeker2", action((faqs: Array<FAQ>): void => {
        copyArray(faqs, this.faqs, (f: FAQ) => { return new FAQ(f); });
      }))
    }
  }
}


// WEBPACK FOOTER //
// ./src/stores/viewState.ts